import PlusSvg from 'images/hypotheekbespaarcheck/plus.svg';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from '../hooks';
import { DeleteButton, LoanpartButton } from 'shared/buttons';
import { Heading, Info, LabelText, StatelessCollapsable } from 'shared/components';
import { InputsContainer } from 'shared/containers';
import { CurrencyInput, RadioSwitch, SelectInput, SplitDate } from 'shared/inputs';
import analytics from 'shared/utility/analytics';
import { ADD_LOANPART, DELETE_LOANPART, LOANPART, SYNC, sync } from 'shared/wizard/actions';

const LoanPart = ({ index, isOpen, setOpenLoanPartIndex }) => {
  const { currentDebt, mortgageType, startDate } = useSelector(({ input: { loanparts } }) => loanparts[index]);
  const splitDate = startDate.split('-');
  const startDateObj = {
    day: splitDate[0] || '',
    month: splitDate[1] || '',
    year: splitDate[2] || '',
    value: startDate,
  };
  const hasMultipleLoanparts = useSelector(({ input: { loanparts } }) => loanparts.length > 1);

  // For this component only 'Aflossingsvrij', 'Lineair', 'Annuïteiten' should be in the options
  // since other tools still use more options these are filtered out for now
  const mortgageTypes = useSelector(({ settings: { meta } }) =>
    meta.mortgage_types.filter(({label}) => ['Aflossingsvrij', 'Lineair', 'Annuïteiten'].includes(label))
    )

  // Only get the errors I need to prevent extra renders,
  // since it are only three these are picked seperately
  const currentDebtError = useSelector(({ output: { errors } }) => errors[`loanparts.${index}.currentDebt`]);
  const mortgageTypeError = useSelector(({ output: { errors } }) => errors[`loanparts.${index}.mortgageType`]);
  const startDateError = useSelector(({ output: { errors } }) => errors[`loanparts.${index}.startDate`]);
  const hasErrors = !!(currentDebtError || mortgageTypeError || startDateError);

  const toggleLoanPart = () => setOpenLoanPartIndex(isOpen ? null : index);
  const dispatch = useDispatch();
  const updateLoanPart = ({ target: { name, value } }) =>
    dispatch({ type: LOANPART, index, payload: { [name]: value } });

  return (
    <StatelessCollapsable
      key={index}
      title={`Leningdeel ${index + 1}`}
      subTitle={'vul gegevens in'}
      hasErrors={hasErrors}
      isOpen={isOpen}
      handleClick={toggleLoanPart}
    >
      <LabelText css={{ marginTop: '8px!important' }}>
        Hypotheekvorm
        <Info title="Hypotheekvorm">
          Een hypotheek kan uit meerdere leningdelen bestaan. Vul hier per leningdeel de gegevens in.
        </Info>
      </LabelText>
      <SelectInput
        name="mortgageType"
        placeholder="Hypotheekvorm"
        errors={mortgageTypeError}
        options={mortgageTypes || []}
        value={mortgageType}
        onChange={({ value }) => updateLoanPart({ target: { name: 'mortgageType', value } })}
      />
      <LabelText>
        Wat is de huidige hoogte van dit leningdeel?
        <Info title="Huidige hoogte leningdeel">
          Vul hier de hoogte van het leningdeel in op het moment van afsluiten van de hypotheek
        </Info>
      </LabelText>
      <CurrencyInput
        name="currentDebt"
        value={currentDebt}
        errors={currentDebtError}
        placeholder="Bedrag"
        onChange={updateLoanPart}
      />
      <LabelText>
        Datum waarop jij dit leningdeel hebt afgesloten
        <Info title="Startdatum leningdeel">
          Het gaat hier om het moment waarop je de hypotheek hebt afgesloten. Dit is van belang omdat de hypotheekrente
          vanaf 1 januari 2001 maximaal 30 jaar fiscaal aftrekbaar is.
        </Info>
      </LabelText>
      <SplitDate
        name="startDate"
        value={startDateObj}
        errors={startDateError}
        onChange={({ value }) => updateLoanPart({ target: { name: 'startDate', value } })}
      />
      {hasMultipleLoanparts && (
        <DeleteButton
          onClick={() => {
            dispatch({ type: DELETE_LOANPART, payload: index });
            setOpenLoanPartIndex(null);
          }}
          type="button"
        >
          Leningdeel verwijderen
        </DeleteButton>
      )}
    </StatelessCollapsable>
  );
};

const CurrentMortgageForm = () => {
  const [openLoanPartIndex, setOpenLoanPartIndex] = useState(0);
  const hasMortgage = useSelector(({ input }) => input.applicant_has_mortgage);
  const houseValue = useSelector(({ input }) => input.current_house_value);
  const loanpartsLength = useSelector(({ input: { loanparts } }) => loanparts.length);
  const hasMortgageError = useSelector(({ output: { errors } }) => errors.applicant_has_mortgage);
  const houseValueError = useSelector(({ output: { errors } }) => errors.current_house_value);
  const dispatch = useDispatch();
  const dispatchSync = sync(dispatch);

  useEffect(() => {
    dispatch({ type: SYNC, syncing: false });
    analytics.send('Navigeer', 'Mortgage Pagina');
  }, []);

  return (
    <>
      <Heading>Je hypotheek</Heading>
      <InputsContainer>
        <LabelText>Heb je een hypotheek?</LabelText>
        <RadioSwitch
          name="applicant_has_mortgage"
          onChangeHandler={dispatchSync('applicant_has_mortgage')}
          checked={hasMortgage}
          errors={hasMortgageError}
        />
        {hasMortgage && (
          <>
            <LabelText>
              Wat is jouw huidige woningwaarde?
              <Info title="Huidige woningwaarde">
                Weet je niet exact wat je huidige woningwaarde is? Vul dan hier de waarde in waarvoor jij het minimaal
                denkt te kunnen verkopen.
              </Info>
            </LabelText>
            <CurrencyInput
              name="current_house_value"
              value={houseValue}
              errors={houseValueError}
              onChange={dispatchSync('current_house_value')}
              placeholder="Waarde"
              type="number"
            />
            <div css={{ marginTop: '32px' }}>
              {Array(loanpartsLength)
                .fill()
                .map((_, index) => (
                  <LoanPart
                    key={index}
                    index={index}
                    isOpen={index === openLoanPartIndex}
                    setOpenLoanPartIndex={setOpenLoanPartIndex}
                  />
                ))}
              <LoanpartButton
                type="button"
                onClick={() => (dispatch({ type: ADD_LOANPART }), setOpenLoanPartIndex(loanpartsLength))}
              >
                <PlusSvg />
                Nog een leningdeel toevoegen
              </LoanpartButton>
            </div>
          </>
        )}
      </InputsContainer>
    </>
  );
};

export default CurrentMortgageForm;
